// E8DivisibilityTruthClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 5) - podzielnosc (P/F).
// Zd.1: suma k+m podzielna przez d1.  Zd.2: iloczyn k*m podzielny przez d2.

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import TruthInput from "../../../../components/inputs/TruthInput";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function buildData({ k, m, d1, d2 }) {
  const sum = k + m;
  const product = k * m;
  return {
    k,
    m,
    d1,
    d2,
    sum,
    product,
    correctPF: [sum % d1 === 0 ? "P" : "F", product % d2 === 0 ? "P" : "F"],
  };
}

function createOriginalTask() {
  return {
    type: "E8DivisibilityTruthClosedTask",
    data: buildData({ k: 69, m: 83, d1: 4, d2: 3 }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 5 },
    tags: ["e8", "teoria-liczb", "podzielnosc", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8DivisibilityTruthClosedTask",
    data: buildData({
      k: randInt(40, 99),
      m: randInt(40, 99),
      d1: pick([3, 4, 5]),
      d2: pick([2, 3, 5]),
    }),
    source: { variant: "random", basedOn: "E8_2025_CZE_5" },
    tags: ["e8", "teoria-liczb", "podzielnosc", "prawda_falsz"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dane są liczby: <InlineMath math={`k=${data.k}`} /> oraz{" "}
        <InlineMath math={`m=${data.m}`} />.
      </p>
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie
        jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>
        1. Suma liczb <InlineMath math="k" /> i <InlineMath math="m" /> jest podzielna
        przez <InlineMath math={`${data.d1}`} />.
      </p>
      <p>
        2. Iloczyn liczb <InlineMath math="k" /> i <InlineMath math="m" /> jest
        podzielny przez <InlineMath math={`${data.d2}`} />.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) => onChange?.({ ...value, [key]: nextValue });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput value={value[key] || null} disabled={disabled} onChange={(nextValue) => setValue(key, nextValue)} />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1];
}

export function renderAnswer({ data }) {
  return <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`} />;
}

export function renderExplanation({ data }) {
  const { k, m, d1, d2, sum, product } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Zdanie 1.</strong> Obliczamy sumę i sprawdzamy podzielność przez <InlineMath math={`${d1}`} />:</p>
      <BlockMath math={`${k}+${m}=${sum}`} />
      <p>
        {sum % d1 === 0
          ? <><InlineMath math={`${sum}:${d1}=${sum / d1}`} />, więc suma dzieli się przez <InlineMath math={`${d1}`} /></>
          : <>liczba <InlineMath math={`${sum}`} /> daje resztę <InlineMath math={`${sum % d1}`} /> z dzielenia przez <InlineMath math={`${d1}`} />, więc nie jest podzielna</>}
        {" "}— zdanie 1. jest {data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}.
      </p>
      <p><strong>Zdanie 2.</strong> Sprawdzamy podzielność iloczynu przez <InlineMath math={`${d2}`} />:</p>
      <BlockMath math={`${k}\\cdot ${m}=${product}`} />
      <p>
        Iloczyn {product % d2 === 0 ? "dzieli się" : "nie dzieli się"} przez{" "}
        <InlineMath math={`${d2}`} /> — zdanie 2. jest {data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}.
      </p>
      <p>Odpowiedź: <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\ 2.\\ \\text{${data.correctPF[1]}}`} />.</p>
    </div>
  );
}
